import { useContext, useState } from "react";
import { InfoContext } from "../../../../InfoContext";
import { AuthContext } from "../../../../AuthContext";
import { useAlert } from "../../../../AlertContext";
import axios from "axios";
import LoadingCircle from "../../../../LoadingCircle";

const server_url = process.env.REACT_APP_BACKEND_URL;

interface InitialGoal {
  id: number;
  user_id: number;
  team_id: number;
  goal_count: number;
}

interface ScorerPopupProps {
  teamId: number;
  matchId: number;
  resultId: number;
  allowedGoals: number;
  initialGoals?: InitialGoal[];
  onClose: () => void;
}

const ScorerPopup: React.FC<ScorerPopupProps> = ({
  teamId,
  matchId,
  resultId,
  initialGoals = [], 
  allowedGoals,
  onClose,
}) => {
  const { getTeamById, fetchSeasons } = useContext(InfoContext);
  const { session } = useContext(AuthContext);
  const { showAlert } = useAlert();
  const team = getTeamById(teamId);
  const players = team?.players ? [...team.players] : [];
  if (
    team?.captain &&
    !players.some((p) => p.id === team.captain?.id)
  ) {
    players.push(team.captain);
  }
  const [goals, setGoals] = useState<{ [playerId: number]: number }>(() => {
    const obj: Record<number, number> = {};
    initialGoals.forEach(({ id, user_id, team_id, goal_count}) => {
      obj[user_id] = goal_count;
    });
    return obj;
  });
  const [isSaving, setIsSaving] = useState(false);

  const totalAssignedGoals = Object.values(goals).reduce(
    (sum, g) => sum + g,
    0
  );

  const handleIncrement = (playerId: number) => {
    if (totalAssignedGoals < allowedGoals) {
      setGoals((prev) => ({ ...prev, [playerId]: (prev[playerId] || 0) + 1 }));
    }
  };

  const handleDecrement = (playerId: number) => {
    const current = goals[playerId] || 0;
    if (current > 0) {
      setGoals((prev) => {
        const newGoals = { ...prev, [playerId]: current - 1 };
        if (newGoals[playerId] === 0) delete newGoals[playerId];
        return newGoals;
      });
    }
  };

  const handleSave = async () => {
    if (totalAssignedGoals !== allowedGoals) {
      showAlert("error", `Please assign exactly ${allowedGoals} goals.`);
      return;
    }

    const scorers = Object.entries(goals)
      .filter(([_, goalCount]) => goalCount > 0)
      .map(([playerId, goalCount]) => ({
        playerId: parseInt(playerId),
        goals: goalCount,
      }));

    setIsSaving(true);
    try {
      const response = await axios.post(`${server_url}/addscorer`, {
        access_token: session.access_token,
        match_id: matchId,
        result_id: resultId,
        team_id: teamId,
        scorers,
      });

      if (response.data.status) {
        showAlert("success", "Scorers added successfully!");
        onClose();
      } else {
        showAlert("error", response.data.error || "Failed to add scorers.");
      }
    } catch (error) {
      showAlert("error", "Error adding scorers. Please try again.");
      console.error(error);
    } finally {
      await fetchSeasons();
      setIsSaving(false);
    }
  };

  if (!team || players.length === 0) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        onClick={onClose}
      >
        <div
          className="bg-white p-4 rounded-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <p>No players found for this team.</p>
          <button
            onClick={onClose}
            className="py-2 px-4 rounded-xl bg-gray-500 hover:bg-gray-600 mt-2 text-white"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20"
      onClick={onClose}
    >
      <div
        className="bg-white p-4 rounded-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4 text-black">Add Scorers</h2>
        <p className="text-black">
          Assigned {totalAssignedGoals} of {allowedGoals} goals.
        </p>
        <div className="max-h-96 overflow-y-auto mt-4 text-black">
          {players.map((player) => (
            <div
              key={player.id}
              className="flex items-center justify-between mb-2"
            >
              <span>{player.name}</span>
              <div className="flex items-center">
                <button
                  onClick={() => handleDecrement(player.id)}
                  disabled={!(goals[player.id] > 0)}
                  className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                >
                  -
                </button>
                <span className="mx-2 w-8 text-center">
                  {goals[player.id] || 0}
                </span>
                <button
                  onClick={() => handleIncrement(player.id)}
                  disabled={totalAssignedGoals >= allowedGoals}
                  className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="py-2 px-4 rounded-xl bg-gray-500 hover:bg-gray-600 mr-2 text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || totalAssignedGoals !== allowedGoals}
            className={`py-2 px-4 rounded-xl bg-green-500 hover:bg-green-600 text-white ${
              isSaving || totalAssignedGoals !== allowedGoals
                ? "opacity-50"
                : ""
            }`}
          >
            {isSaving ? <LoadingCircle small={true} /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
export default ScorerPopup;
